import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Plus, Trash2, Loader2, Building2, Layers, Sparkles, PlusCircle } from 'lucide-react';

interface CandidateSkill {
  id: string;
  skill_id: string;
  proficiency_level?: string | null;
  years_experience?: number | null;
  experience_id?: string | null;
  skills: {
    id: string;
    name: string;
    type: string | null;
  };
}

interface Skill {
  id: string;
  name: string;
  type: string | null;
}

interface Experience {
  id: string;
  job_title: string;
  company_id: string | null;
  companies?: { name: string } | null;
}

interface EditableSkillsProps {
  candidateId: string;
  skills: CandidateSkill[];
  onUpdate: () => void;
}

const EditableSkills = ({ candidateId, skills, onUpdate }: EditableSkillsProps) => {
  const { toast } = useToast();
  const [allSkills, setAllSkills] = useState<Skill[]>([]);
  const [experiences, setExperiences] = useState<Experience[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedSkill, setSelectedSkill] = useState<CandidateSkill | null>(null);
  const [saving, setSaving] = useState(false);
  const [autoLinking, setAutoLinking] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<'type' | 'company'>('type');
  const [createNewSkill, setCreateNewSkill] = useState(false);
  const [newSkillName, setNewSkillName] = useState('');
  const [newSkillType, setNewSkillType] = useState('technical');
  const [formData, setFormData] = useState({
    skill_id: '',
    proficiency_level: 'intermediate',
    years_experience: '',
    experience_id: '',
  });

  useEffect(() => {
    fetchAllSkills();
    fetchExperiences();
  }, [candidateId]);

  const fetchAllSkills = async () => {
    const { data } = await supabase.from('skills').select('id, name, type').order('name');
    if (data) setAllSkills(data);
  };

  const fetchExperiences = async () => {
    const { data } = await supabase
      .from('candidate_experience')
      .select('id, job_title, company_id, companies(name)')
      .eq('candidate_id', candidateId)
      .order('start_date', { ascending: false });
    if (data) {
      const mapped = data.map(exp => ({
        id: exp.id,
        job_title: exp.job_title,
        company_id: exp.company_id,
        companies: Array.isArray(exp.companies) ? exp.companies[0] : exp.companies
      }));
      setExperiences(mapped);
    }
  };

  const openAdd = (preselectedExperienceId?: string) => {
    setFormData({
      skill_id: '',
      proficiency_level: 'intermediate',
      years_experience: '',
      experience_id: preselectedExperienceId || '',
    });
    setSearchQuery('');
    setCreateNewSkill(false);
    setNewSkillName('');
    setNewSkillType('technical');
    setDialogOpen(true);
  };

  const openDelete = (skill: CandidateSkill) => {
    setSelectedSkill(skill);
    setDeleteDialogOpen(true);
  };

  const handleAutoLinkSkills = async () => {
    setAutoLinking(true);
    try {
      const { data, error } = await supabase.functions.invoke('auto-link-skills', {
        body: { candidateId }
      });

      if (error) throw error;

      toast({ 
        title: 'Skills Auto-Linked', 
        description: data.message || `Linked ${data.linked} skills to experiences`
      });
      onUpdate();
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } finally {
      setAutoLinking(false);
    }
  };

  const handleSave = async () => {
    let skillIdToUse = formData.skill_id;

    // Create new skill if needed
    if (createNewSkill) {
      if (!newSkillName.trim()) {
        toast({ title: 'Please enter a skill name', variant: 'destructive' });
        return;
      }

      setSaving(true);
      try {
        // Check if skill already exists
        const { data: existing } = await supabase
          .from('skills')
          .select('id')
          .ilike('name', newSkillName.trim())
          .maybeSingle();

        if (existing) {
          skillIdToUse = existing.id;
        } else {
          const { data: newSkill, error: createError } = await supabase
            .from('skills')
            .insert({ name: newSkillName.trim(), type: newSkillType as any })
            .select('id')
            .single();

          if (createError) throw createError;
          skillIdToUse = newSkill.id;
          
          // Refresh skills list
          fetchAllSkills();
        }
      } catch (error: any) {
        toast({ title: 'Error creating skill', description: error.message, variant: 'destructive' });
        setSaving(false);
        return;
      }
    }

    if (!skillIdToUse) {
      toast({ title: 'Please select or create a skill', variant: 'destructive' });
      return;
    }

    // Check if skill already exists for this experience (or as general)
    const exists = skills.some(s => 
      s.skill_id === skillIdToUse && 
      (s.experience_id || '') === formData.experience_id
    );
    if (exists) {
      toast({ title: 'Skill already added for this experience', variant: 'destructive' });
      setSaving(false);
      return;
    }

    setSaving(true);
    try {
      const { error } = await supabase
        .from('candidate_skills')
        .insert({
          candidate_id: candidateId,
          skill_id: skillIdToUse,
          proficiency_level: formData.proficiency_level,
          years_experience: formData.years_experience ? parseFloat(formData.years_experience) : null,
          experience_id: formData.experience_id || null,
        });
      if (error) throw error;
      toast({ title: 'Skill added' });
      setDialogOpen(false);
      onUpdate();
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedSkill) return;
    
    try {
      const { error } = await supabase
        .from('candidate_skills')
        .delete()
        .eq('id', selectedSkill.id);
      if (error) throw error;
      toast({ title: 'Skill removed' });
      setDeleteDialogOpen(false);
      onUpdate();
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  const filteredSkills = allSkills.filter(s => 
    s.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Group skills by type
  const groupedByType = skills.reduce((acc, skill) => {
    const type = skill.skills.type || 'other';
    if (!acc[type]) acc[type] = [];
    acc[type].push(skill);
    return acc;
  }, {} as Record<string, CandidateSkill[]>);

  // Group skills by company/experience
  const groupedByCompany = skills.reduce((acc, skill) => {
    if (skill.experience_id) {
      const exp = experiences.find(e => e.id === skill.experience_id);
      const companyName = exp?.companies?.name || exp?.job_title || 'Unknown';
      if (!acc[companyName]) acc[companyName] = { experienceId: skill.experience_id, skills: [] };
      acc[companyName].skills.push(skill);
    } else {
      if (!acc['General Skills']) acc['General Skills'] = { experienceId: null, skills: [] };
      acc['General Skills'].skills.push(skill);
    }
    return acc;
  }, {} as Record<string, { experienceId: string | null; skills: CandidateSkill[] }>);

  const renderSkillBadge = (cs: CandidateSkill) => (
    <Badge key={cs.id} variant="secondary" className="flex items-center gap-1 pr-1">
      {cs.skills.name}
      {cs.years_experience && (
        <span className="opacity-70">({cs.years_experience}y)</span>
      )}
      {cs.proficiency_level && cs.proficiency_level !== 'intermediate' && (
        <span className="opacity-60 text-xs capitalize">• {cs.proficiency_level}</span>
      )}
      <Button 
        size="sm" 
        variant="ghost" 
        className="h-4 w-4 p-0 ml-1 hover:bg-destructive/20"
        onClick={() => openDelete(cs)}
      >
        <Trash2 className="h-3 w-3 text-destructive" />
      </Button>
    </Badge>
  );

  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center gap-2 flex-wrap">
        <Tabs value={viewMode} onValueChange={(v) => setViewMode(v as 'type' | 'company')} className="w-auto">
          <TabsList className="h-8">
            <TabsTrigger value="type" className="text-xs px-3">
              <Layers className="h-3 w-3 mr-1" />
              By Type
            </TabsTrigger>
            <TabsTrigger value="company" className="text-xs px-3">
              <Building2 className="h-3 w-3 mr-1" />
              By Company
            </TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="flex gap-2">
          <Button 
            size="sm" 
            variant="outline" 
            onClick={handleAutoLinkSkills}
            disabled={autoLinking}
            className="text-xs"
          >
            {autoLinking ? (
              <Loader2 className="h-3 w-3 mr-1 animate-spin" />
            ) : (
              <Sparkles className="h-3 w-3 mr-1" />
            )}
            Auto-Link Skills
          </Button>
          <Button size="sm" variant="outline" onClick={() => openAdd()}>
            <Plus className="h-4 w-4 mr-1" />
            Add Skill
          </Button>
        </div>
      </div>

      {skills.length === 0 ? (
        <p className="text-center text-muted-foreground py-8">No skills listed</p>
      ) : viewMode === 'type' ? (
        // View by Type
        Object.entries(groupedByType).map(([type, typeSkills]) => (
          <Card key={type} className="p-4">
            <h4 className="font-semibold text-sm mb-3 capitalize">{type.replace(/_/g, ' ')}</h4>
            <div className="flex flex-wrap gap-2">
              {typeSkills.map(renderSkillBadge)}
            </div>
          </Card>
        ))
      ) : (
        // View by Company
        <>
          {experiences.map((exp) => {
            const companyName = exp.companies?.name || exp.job_title;
            const companyData = groupedByCompany[companyName];
            const companySkills = companyData?.skills || [];
            
            return (
              <Card key={exp.id} className="p-4">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <h4 className="font-semibold text-sm flex items-center gap-2">
                      <Building2 className="h-4 w-4 text-muted-foreground" />
                      {companyName}
                    </h4>
                    <p className="text-xs text-muted-foreground">{exp.job_title}</p>
                  </div>
                  <Button 
                    size="sm" 
                    variant="ghost" 
                    className="h-6 text-xs"
                    onClick={() => openAdd(exp.id)}
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    Add
                  </Button>
                </div>
                {companySkills.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {companySkills.map(renderSkillBadge)}
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground italic">No skills linked to this experience</p>
                )}
              </Card>
            );
          })}

          {/* General Skills (not linked to any experience) */}
          {groupedByCompany['General Skills']?.skills.length > 0 && (
            <Card className="p-4 border-dashed">
              <h4 className="font-semibold text-sm mb-3 flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-muted-foreground"></span>
                General Skills
                <span className="text-xs text-muted-foreground font-normal">
                  (not linked to any company)
                </span>
              </h4>
              <div className="flex flex-wrap gap-2">
                {groupedByCompany['General Skills'].skills.map(renderSkillBadge)}
              </div>
            </Card>
          )}
        </>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Skill</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Toggle between existing and new skill */}
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant={!createNewSkill ? "default" : "outline"}
                size="sm"
                onClick={() => setCreateNewSkill(false)}
              >
                Choose Existing
              </Button>
              <Button
                type="button"
                variant={createNewSkill ? "default" : "outline"}
                size="sm"
                onClick={() => setCreateNewSkill(true)}
              >
                <PlusCircle className="h-3 w-3 mr-1" />
                Create New
              </Button>
            </div>

            {!createNewSkill ? (
              <div>
                <Label>Search & Select Skill *</Label>
                <Input 
                  placeholder="Search skills..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="mb-2"
                />
                <Select 
                  value={formData.skill_id} 
                  onValueChange={(v) => setFormData({ ...formData, skill_id: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a skill" />
                  </SelectTrigger>
                  <SelectContent className="max-h-60">
                    {filteredSkills.slice(0, 50).map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.name} {s.type && <span className="text-muted-foreground">({s.type})</span>}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div className="space-y-3">
                <div>
                  <Label>New Skill Name *</Label>
                  <Input 
                    placeholder="e.g., React, Python, AWS..."
                    value={newSkillName}
                    onChange={(e) => setNewSkillName(e.target.value)}
                  />
                </div>
                <div>
                  <Label>Skill Type</Label>
                  <Select value={newSkillType} onValueChange={setNewSkillType}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="technical">Technical</SelectItem>
                      <SelectItem value="tool">Tool</SelectItem>
                      <SelectItem value="framework">Framework</SelectItem>
                      <SelectItem value="language">Language</SelectItem>
                      <SelectItem value="soft">Soft Skill</SelectItem>
                      <SelectItem value="certification">Certification</SelectItem>
                      <SelectItem value="product">Product</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            <div>
              <Label>Link to Experience (Optional)</Label>
              <Select 
                value={formData.experience_id} 
                onValueChange={(v) => setFormData({ ...formData, experience_id: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="General skill (no company)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">General skill (no company)</SelectItem>
                  {experiences.map((exp) => (
                    <SelectItem key={exp.id} value={exp.id}>
                      {exp.companies?.name || 'Unknown'} - {exp.job_title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">
                Link this skill to a specific work experience/company
              </p>
            </div>

            <div>
              <Label>Proficiency Level</Label>
              <Select 
                value={formData.proficiency_level} 
                onValueChange={(v) => setFormData({ ...formData, proficiency_level: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="beginner">Beginner</SelectItem>
                  <SelectItem value="intermediate">Intermediate</SelectItem>
                  <SelectItem value="advanced">Advanced</SelectItem>
                  <SelectItem value="expert">Expert</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Years of Experience</Label>
              <Input 
                type="number"
                step="0.5"
                value={formData.years_experience}
                onChange={(e) => setFormData({ ...formData, years_experience: e.target.value })}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                Add
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Skill</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove "{selectedSkill?.skills.name}" from this candidate's profile?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default EditableSkills;
